/**
 * Health Color Utilities
 * 
 * Provides consistent color mapping for health scores across the Care360 application.
 * Implements a 7-color gradient spectrum for smooth health visualization.
 */

/**
 * Get color for health score using 7-color gradient spectrum
 * @param {number} score - Health score (0-100)
 * @returns {string} - Hex color code
 */
export const getHealthScoreColor = (score) => {
  // Handle invalid scores
  if (score === null || score === undefined || isNaN(score)) {
    return '#d9d9d9'; // Gray for invalid/missing data
  }
  
  // Clamp score to 0-100 range
  const clampedScore = Math.max(0, Math.min(100, score));
  
  // 7-color gradient progression for smooth health visualization
  if (clampedScore >= 90) {
    return '#22c55e'; // Green (100-90%) - clearly healthy
  } else if (clampedScore >= 80) {
    return '#84cc16'; // Light green/yellow-green (89-80%) - good health
  } else if (clampedScore >= 70) {
    return '#eab308'; // Yellow (79-70%) - moderate health
  } else if (clampedScore >= 60) {
    return '#f59e0b'; // Yellow-orange (69-60%) - concerning
  } else if (clampedScore >= 50) {
    return '#f97316'; // Orange (59-50%) - poor health
  } else if (clampedScore >= 40) {
    return '#ef4444'; // Orange-red (49-40%) - very poor health
  } else {
    return '#dc2626'; // Red (39-0%) - critical health issues
  }
};

/**
 * Get health status description based on score
 * @param {number} score - Health score (0-100)
 * @returns {object} - Object with level, color, and icon
 */
export const getHealthStatusInfo = (score) => {
  const color = getHealthScoreColor(score);
  
  if (score === null || score === undefined || isNaN(score)) {
    return { level: "No Data", color: '#d9d9d9', icon: "❓" };
  }
  
  const clampedScore = Math.max(0, Math.min(100, score));
  
  if (clampedScore >= 90) {
    return { level: "Excellent", color, icon: "🌟" };
  } else if (clampedScore >= 80) {
    return { level: "Very Good", color, icon: "✅" };
  } else if (clampedScore >= 70) {
    return { level: "Good", color, icon: "👍" };
  } else if (clampedScore >= 60) {
    return { level: "Fair", color, icon: "⚠️" };
  } else if (clampedScore >= 50) {
    return { level: "Poor", color, icon: "🔶" };
  } else if (clampedScore >= 40) {
    return { level: "Very Poor", color, icon: "🔴" };
  } else {
    return { level: "Critical", color, icon: "🚨" };
  }
};

/**
 * Color palette definitions for reference
 */
export const HEALTH_COLOR_PALETTE = {
  EXCELLENT: '#22c55e',      // Green (100-90%)
  VERY_GOOD: '#84cc16',      // Light green/yellow-green (89-80%)
  GOOD: '#eab308',           // Yellow (79-70%)
  FAIR: '#f59e0b',           // Yellow-orange (69-60%)
  POOR: '#f97316',           // Orange (59-50%)
  VERY_POOR: '#ef4444',      // Orange-red (49-40%)
  CRITICAL: '#dc2626',       // Red (39-0%)
  NO_DATA: '#d9d9d9'         // Gray for invalid/missing data
};

/**
 * Get color with opacity for backgrounds
 * @param {number} score - Health score (0-100)
 * @param {number} opacity - Opacity value (0-1)
 * @returns {string} - RGBA color string
 */
export const getHealthScoreColorWithOpacity = (score, opacity = 0.1) => {
  const color = getHealthScoreColor(score);
  
  // Convert hex to RGB and add opacity
  const hex = color.replace('#', '');
  const r = parseInt(hex.substr(0, 2), 16);
  const g = parseInt(hex.substr(2, 2), 16);
  const b = parseInt(hex.substr(4, 2), 16);
  
  return `rgba(${r}, ${g}, ${b}, ${opacity})`;
};

/**
 * Check if a color represents a healthy score (80% or above)
 * @param {number} score - Health score (0-100)
 * @returns {boolean} - True if score represents good health
 */
export const isHealthyScore = (score) => {
  if (score === null || score === undefined || isNaN(score)) {
    return false;
  }
  return score >= 80;
};

/**
 * Check if a color represents a concerning score (below 60%)
 * @param {number} score - Health score (0-100)
 * @returns {boolean} - True if score represents concerning health
 */
export const isConcerningScore = (score) => {
  if (score === null || score === undefined || isNaN(score)) {
    return false;
  }
  return score < 60;
};
